import React, { useEffect } from "react";
import {
  Box,
  Button,
  Divider,
  Grid,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import TopBar from "../../../components/TopBar";
import { useSelector } from "react-redux";
import { map } from "lodash";
import { useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import CustomTextInput from "../../../components/CustomTextInput";
import CountrySelect from "../../../components/form/CountrySelect";
import OMNSubdivisionSelect from "../../../components/form/OMNSubdivisionSelect";
import RegistrationTypeSelect from "../../../components/form/RegistrationTypeSelect";
import { CustomerValidation } from "../../../validations/CustomerValidation";
import {
  getCountryOptionByCode,
  getRegistrationTypeOptionByCode,
  getB2BRegistrationTypeOptionsExcludingOTH,
  getSubdivisionOptionByCode,
  getEndpointSchemeIdByCode,
} from "../../../api/codelist/codeListsSlice";
import TaxSchemeSelect from "../../../components/form/TaxSchemeSelect";
import EndpointSchemeIdSelect from "../../../components/form/EndpointSchemeIdSelect";
import {
  OMN_COUNTRY_CODE,
  DEFAULT_CUSTOMER_TYPE,
} from "../../../constants/defaults";

const DEFAULT_ENDPOINT_SCHEME_ID = "0248";

const isPresentValue = (value) => {
  if (value == null) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (typeof value === "object") {
    if ("code" in value) return !!value.code;
    return false;
  }
  return true;
};

export default function CustomerForm({
  onSubmit,
  onCancel,
  initialData = {},
  apiErrors = {},
  edit = false,
  readOnly = false,
  customerType = DEFAULT_CUSTOMER_TYPE,
  isLoading = false,
}) {
  const theme = useTheme();
  const isIndividual = customerType === "INDIVIDUAL";
  const hideEmptyOptional = readOnly && isIndividual;
  const showOptional = (value) => !hideEmptyOptional || isPresentValue(value);

  const countryOption = useSelector(
    getCountryOptionByCode(initialData.postal_address?.country_code),
  );
  const subdivisionOption = useSelector(
    getSubdivisionOptionByCode(initialData.postal_address?.country_subentity),
  );
  const schemeAgencyIdOption = useSelector(
    getRegistrationTypeOptionByCode(initialData.scheme_agency_id),
  );
  const registrationTypeOption = useSelector(
    getRegistrationTypeOptionByCode(initialData.registration_type),
  );
  const passportIssuingCountryOption = useSelector(
    getCountryOptionByCode(initialData.passport_issuing_country_code),
  );
  const b2bOptionsExcludingOTH = useSelector(
    getB2BRegistrationTypeOptionsExcludingOTH,
  );
  const endpointSchemeOption = useSelector(
    getEndpointSchemeIdByCode(
      initialData.endpoint_scheme_id || DEFAULT_ENDPOINT_SCHEME_ID,
    ),
  );

  const {
    control,
    setValue,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(CustomerValidation(isIndividual)),
    defaultValues: {
      ...initialData,
      customer_type: customerType,
      postal_address: {
        ...initialData.postal_address,
        street_name: initialData.postal_address?.street_name,
        additional_street_name:
          initialData.postal_address?.additional_street_name,
        address_line: initialData.postal_address?.address_line,
        city_name: initialData.postal_address?.city_name,
        postal_zone: initialData.postal_address?.postal_zone,
        country_code: countryOption,
        country_subentity:
          initialData.postal_address?.country_code === OMN_COUNTRY_CODE
            ? (subdivisionOption ?? null)
            : initialData.postal_address?.country_subentity,
      },
      telephone: initialData.telephone,
      scheme_agency_id: schemeAgencyIdOption,
      registration_type: registrationTypeOption,
      passport_issuing_country_code: passportIssuingCountryOption,
      endpoint_scheme_id: endpointSchemeOption ?? null,
      endpoint_id: initialData.endpoint_id || "",
    },
  });

  useEffect(() => {
    map(apiErrors, (value, key) => {
      setError(key, { message: value, type: "string" });
    });
  }, [setError, apiErrors]);

  const handleCountryChange = (value) => {
    const isOMN = value?.code === OMN_COUNTRY_CODE;
    setValue("postal_address.country_subentity", isOMN ? null : "");
    if (!isIndividual) {
      setValue("scheme_agency_id", null);
      setValue("company_id", "");
      if (isOMN) {
        setValue(
          "endpoint_scheme_id",
          endpointSchemeOption?.code === DEFAULT_ENDPOINT_SCHEME_ID
            ? endpointSchemeOption
            : { code: DEFAULT_ENDPOINT_SCHEME_ID, label: "Oman VAT" },
        );
      } else {
        setValue("party_tax_scheme.tax_scheme", "");
        setValue("party_tax_scheme.company_id", "");
        setValue("endpoint_scheme_id", null);
        setValue("endpoint_id", "");
      }
    }
  };

  const handleRegisterType = () => {
    setValue("company_id", "");
  };

  const handleFormSubmit = (data) => {
    const isOMN = data.postal_address?.country_code?.code === OMN_COUNTRY_CODE;
    const vatIdentifier =
      data.party_tax_scheme?.company_id || initialData?.endpoint_id || "";
    const submitData = {
      ...data,
      customer_type: customerType,
      scheme_agency_id: data.scheme_agency_id?.code || undefined,
      company_id: data.company_id || undefined,
      postal_address: {
        ...data.postal_address,
        country_code: data.postal_address?.country_code?.code,
        country: data.postal_address?.country_code?.label,
        country_subentity:
          data.postal_address?.country_code?.code === OMN_COUNTRY_CODE
            ? data.postal_address?.country_subentity?.code
            : data.postal_address?.country_subentity,
      },
      passport_issuing_country_code: data.passport_issuing_country_code?.code,
      endpoint_scheme_id:
        data.endpoint_scheme_id?.code || data.endpoint_scheme_id,
      endpoint_id: data.endpoint_id,
    };
    if (isOMN && !isIndividual) {
      submitData.endpoint_id = vatIdentifier || data.endpoint_id;
      submitData.endpoint_scheme_id = DEFAULT_ENDPOINT_SCHEME_ID;
      submitData.party_tax_scheme = {
        tax_scheme: data.party_tax_scheme?.tax_scheme,
        company_id: vatIdentifier,
      };
    } else {
      delete submitData.party_tax_scheme;
    }
    delete submitData.tin;
    onSubmit(submitData);
  };

  const registrationType = useWatch({ control, name: "scheme_agency_id" });
  const countryCode = useWatch({
    control,
    name: "postal_address.country_code",
  })?.code;
  const vatId = useWatch({ control, name: "party_tax_scheme.company_id" });
  const streetName = useWatch({
    control,
    name: "postal_address.street_name",
  });
  const additionalStreetName = useWatch({
    control,
    name: "postal_address.additional_street_name",
  });
  const cityName = useWatch({ control, name: "postal_address.city_name" });
  const addressLine = useWatch({
    control,
    name: "postal_address.address_line",
  });
  const countrySubentity = useWatch({
    control,
    name: "postal_address.country_subentity",
  });
  const postalZone = useWatch({
    control,
    name: "postal_address.postal_zone",
  });
  const telephone = useWatch({ control, name: "telephone" });
  const companyId = useWatch({ control, name: "company_id" });
  const passportIssuingCountryCode = useWatch({
    control,
    name: "passport_issuing_country_code",
  });
  const isOMNCountry = countryCode === OMN_COUNTRY_CODE;
  const showBusinessIdentifiersSection =
    !hideEmptyOptional ||
    showOptional(registrationType) ||
    showOptional(companyId) ||
    showOptional(passportIssuingCountryCode);

  useEffect(() => {
    if (!isIndividual && isOMNCountry && vatId) {
      setValue("endpoint_id", vatId);
      if (endpointSchemeOption) {
        setValue("endpoint_scheme_id", endpointSchemeOption);
      }
    }
  }, [vatId, isOMNCountry, isIndividual, setValue, endpointSchemeOption]);

  const registrationNumberLabel = registrationType
    ? registrationType.code
      ? `${registrationType.label} (${registrationType.code})`
      : registrationType.label
    : "Registration Number";

  return (
    <Paper elevation={0} sx={{ p: "1rem" }}>
      {!readOnly && (
        <Box sx={{ pt: 2, pb: 4 }}>
          <Grid
            container
            sx={{
              display: "block",
            }}
          >
            <Grid xs={12}>
              <TopBar
                title={"Customer Form"}
                identifier={initialData?.profile_id}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: theme.spacing(1),
                  }}
                >
                  <Button
                    data-testid="customer-profile-cancel-button"
                    key={"cancel"}
                    variant="outlined"
                    onClick={onCancel}
                    disabled={isLoading}
                  >
                    {"Cancel"}
                  </Button>
                  <Button
                    data-testid="customer-profile-save-button"
                    key={"save"}
                    variant="contained"
                    onClick={handleSubmit(handleFormSubmit)}
                    disabled={isLoading}
                  >
                    {"Save"}
                  </Button>
                </Box>
              </TopBar>
            </Grid>
          </Grid>
        </Box>
      )}
      <Box>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              data-testid="name-input"
              name="name"
              control={control}
              errors={errors}
              readOnly={readOnly}
              label={"Name"}
            />
          </Grid>
          {!isIndividual && (
            <>
              <Grid size={{ xs: 12, sm: 6 }}>
                <CustomTextInput
                  data-testid="business-name-input"
                  name="party_name"
                  control={control}
                  errors={errors}
                  readOnly={readOnly}
                  label={"Business Name"}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <CustomTextInput
                  data-testid="business-name-in-local-language-input"
                  name="party_name_in_local_language"
                  control={control}
                  errors={errors}
                  readOnly={readOnly}
                  label={"Business Name in Local Language"}
                />
              </Grid>
            </>
          )}
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              data-testid="email-input"
              name="email"
              control={control}
              errors={errors}
              readOnly={readOnly}
              label={"Email"}
            />
          </Grid>
        </Grid>

        <Typography variant="h6" sx={{ mb: "1.5rem", pt: "1rem" }}>
          {"Address"}
          <Divider></Divider>
        </Typography>

        <Grid container spacing={2}>
          {showOptional(streetName) && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <CustomTextInput
                data-testid="street_name-input"
                name="postal_address.street_name"
                control={control}
                errors={errors}
                readOnly={readOnly}
                label={"Street Name"}
              />
            </Grid>
          )}
          {showOptional(additionalStreetName) && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <CustomTextInput
                data-testid="additional_street_name-input"
                name="postal_address.additional_street_name"
                control={control}
                errors={errors}
                readOnly={readOnly}
                label={"Additional Street Name"}
              />
            </Grid>
          )}
          {showOptional(cityName) && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <CustomTextInput
                data-testid="city_name-input"
                name="postal_address.city_name"
                control={control}
                errors={errors}
                readOnly={readOnly}
                label={"City Name"}
              />
            </Grid>
          )}
          {showOptional(addressLine) && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <CustomTextInput
                name="postal_address.address_line"
                control={control}
                errors={errors}
                readOnly={readOnly}
                label={"Address Line"}
              />
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6 }}>
            <CountrySelect
              data-testid="postal_address.country_code-select"
              name="postal_address.country_code"
              control={control}
              errors={errors}
              readOnly={readOnly || edit}
              label={"Country"}
              onChange={handleCountryChange}
            />
          </Grid>
          {showOptional(countrySubentity) && (
            <Grid size={{ xs: 12, sm: 6 }}>
              {countryCode === OMN_COUNTRY_CODE ? (
                <OMNSubdivisionSelect
                  data-testid="country-subentity-select"
                  name="postal_address.country_subentity"
                  control={control}
                  errors={errors}
                  readOnly={readOnly}
                  label={"Country Subentity"}
                />
              ) : (
                <CustomTextInput
                  data-testid="country-subentity-input"
                  name="postal_address.country_subentity"
                  control={control}
                  errors={errors}
                  readOnly={readOnly}
                  label={"Country Subentity"}
                />
              )}
            </Grid>
          )}
          {showOptional(postalZone) && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <CustomTextInput
                data-testid="postal_zone-input"
                name="postal_address.postal_zone"
                control={control}
                errors={errors}
                readOnly={readOnly}
                label={"Postal Zone"}
              />
            </Grid>
          )}
          {showOptional(telephone) && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <CustomTextInput
                data-tetsid="telephone-input"
                name="telephone"
                control={control}
                errors={errors}
                readOnly={readOnly}
                label={"Telephone"}
              />
            </Grid>
          )}
        </Grid>

        {showBusinessIdentifiersSection && (
          <>
            <Typography variant="h6" sx={{ mb: "1.5rem", pt: "1rem" }}>
              {"Business Identifiers"}
              <Divider></Divider>
            </Typography>

            <Grid container spacing={2}>
              {!isIndividual && countryCode === OMN_COUNTRY_CODE && (
                <>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TaxSchemeSelect
                      data-testid="tax_scheme-select"
                      name="party_tax_scheme.tax_scheme"
                      control={control}
                      errors={errors}
                      readOnly={readOnly}
                      label={"Tax Scheme"}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <CustomTextInput
                      data-testid="tax-identifier-input"
                      name="party_tax_scheme.company_id"
                      control={control}
                      errors={errors}
                      readOnly={readOnly}
                      label={"VAT"}
                      inputProps={{ maxLength: 12 }}
                    />
                  </Grid>
                </>
              )}
              {showOptional(registrationType) && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <RegistrationTypeSelect
                    data-testid="registration-type-select"
                    errors={errors}
                    readOnly={readOnly}
                    control={control}
                    name="scheme_agency_id"
                    customerType={customerType}
                    onChange={handleRegisterType}
                    overrideOptions={
                      !isIndividual && countryCode === OMN_COUNTRY_CODE
                        ? b2bOptionsExcludingOTH
                        : undefined
                    }
                  />
                </Grid>
              )}
              {showOptional(companyId) && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <CustomTextInput
                    data-testid="registration-number-input"
                    name="company_id"
                    control={control}
                    errors={errors}
                    readOnly={readOnly}
                    label={registrationNumberLabel}
                  />
                </Grid>
              )}
              {isIndividual && showOptional(passportIssuingCountryCode) && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <CountrySelect
                    data-testid="passport_issuing_country_code-select"
                    name="passport_issuing_country_code"
                    control={control}
                    errors={errors}
                    readOnly={readOnly}
                    label={"Passport Issuing Country"}
                    disabled={registrationType?.code !== "PASNUM"}
                  />
                </Grid>
              )}
              {!isIndividual && (
                <>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <EndpointSchemeIdSelect
                      data-testid="endpoint_scheme_id-select"
                      name="endpoint_scheme_id"
                      control={control}
                      errors={errors}
                      readOnly={readOnly}
                      disabled={readOnly || isOMNCountry}
                      label={"Endpoint Scheme Id"}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <CustomTextInput
                      data-testid="endpoint_id-input"
                      name="endpoint_id"
                      control={control}
                      errors={errors}
                      readOnly={edit || isOMNCountry}
                      label={"Endpoint Id"}
                      helperText={
                        edit ? "VAT cannot be changed after creation" : ""
                      }
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </>
        )}
      </Box>
    </Paper>
  );
}
